import { useEffect } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { api } from '@/shared/lib/api';
import { randomUUID } from '@/shared/lib/uuid';

export type CashAccountKind = 'cash' | 'bank';
export type CashDirection = 'income' | 'expense';
export type CashCategoryScope = 'income' | 'expense' | 'both';
export type CounterpartyType = 'customer' | 'employee' | 'supplier' | 'partner' | 'other';
export type PaymentMethod = 'cash' | 'bank_transfer' | 'card';

export type MethodBalances = { cash: string; bank_transfer: string; card: string };

export type CashAccount = {
  id: string;
  name: string;
  kind: CashAccountKind;
  bankName: string | null;
  accountNumber: string | null;
  currency: string;
  openingBalance: string;
  isPrimary: boolean;
  active: boolean;
  balance: string;
  methodBalances?: MethodBalances; // present on detail (GET /cash-accounts/:id)
};

export type CashCategory = {
  id: string;
  name: string;
  scope: CashCategoryScope;
  requiresCounterparty: boolean;
  isSystem: boolean;
  sortOrder: number;
  active: boolean;
};

export type Counterparty = {
  id: string;
  name: string;
  type: CounterpartyType;
  phone: string | null;
  note: string | null;
  customerId: string | null;
  active: boolean;
};

export type CashTransaction = {
  id: string;
  cashAccountId: string;
  direction: CashDirection;
  amount: string;
  txnDate: string;
  categoryId: string;
  method: PaymentMethod;
  counterpartyId: string | null;
  description: string | null;
  attachmentUrls: string[];
  sourcePaymentId: string | null;
  excludeFromBalance: boolean;
  voidedAt: string | null;
  voidReason: string | null;
  createdAt: string;
  category?: { id: string; name: string; scope: CashCategoryScope };
  counterparty?: { id: string; name: string; type: CounterpartyType; customerId: string | null } | null;
  cashAccount?: { id: string; name: string; kind: CashAccountKind };
};

// ---------- Accounts ----------
export function useCashAccounts() {
  return useQuery({
    queryKey: ['cash-accounts'],
    queryFn: async () => (await api.get<{ data: CashAccount[] }>('/cash-accounts')).data.data,
  });
}

export function useCashAccount(id: string | undefined) {
  return useQuery({
    queryKey: ['cash-account', id],
    enabled: !!id,
    queryFn: async () => (await api.get<{ data: CashAccount }>(`/cash-accounts/${id}`)).data.data,
  });
}

export function useCreateCashAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<CashAccount> & { name: string; kind: CashAccountKind }) =>
      (await api.post<{ data: CashAccount }>('/cash-accounts', input)).data.data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['cash-accounts'] }),
  });
}

export function useUpdateCashAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: Partial<CashAccount> }) =>
      (await api.patch<{ data: CashAccount }>(`/cash-accounts/${id}`, input)).data.data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['cash-accounts'] }),
  });
}

// ---------- Categories ----------
export function useCashCategories() {
  return useQuery({
    queryKey: ['cash-categories'],
    queryFn: async () => (await api.get<{ data: CashCategory[] }>('/cash-categories')).data.data,
  });
}

export function useCreateCashCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<CashCategory> & { name: string }) =>
      (await api.post<{ data: CashCategory }>('/cash-categories', input)).data.data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['cash-categories'] }),
  });
}

export function useUpdateCashCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: Partial<CashCategory> }) =>
      (await api.patch<{ data: CashCategory }>(`/cash-categories/${id}`, input)).data.data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['cash-categories'] }),
  });
}

export function useDeleteCashCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { await api.delete(`/cash-categories/${id}`); },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['cash-categories'] }),
  });
}

// ---------- Counterparties ----------
export function useCounterparties(params?: { search?: string; type?: CounterpartyType }) {
  return useQuery({
    queryKey: ['counterparties', params],
    queryFn: async () =>
      (await api.get<{ data: Counterparty[] }>('/counterparties', { params })).data.data,
  });
}

export function useCreateCounterparty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<Counterparty> & { name: string }) =>
      (await api.post<{ data: Counterparty }>('/counterparties', input)).data.data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['counterparties'] }),
  });
}

export function useUpdateCounterparty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: Partial<Counterparty> }) =>
      (await api.patch<{ data: Counterparty }>(`/counterparties/${id}`, input)).data.data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['counterparties'] }),
  });
}

export function useDeleteCounterparty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { await api.delete(`/counterparties/${id}`); },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['counterparties'] }),
  });
}

// ---------- Transactions ----------
export type CreateCashTransactionInput = {
  cashAccountId: string;
  direction: CashDirection;
  amount: string;
  txnDate: string;
  categoryId: string;
  method: PaymentMethod;
  counterpartyId?: string;
  description?: string;
  attachmentUrls?: string[];
};

type CashTxnPage = {
  data: CashTransaction[];
  pagination: { cursor: string | null; hasMore: boolean };
};

const CASH_TXN_PAGE_SIZE = 500;

/**
 * Loads the full filtered ledger by walking the backend cursor across pages,
 * not just the first page. Without this the list only ever showed the newest
 * ~200 rows, so older transactions (even inside a selected date range) were
 * silently hidden.
 */
export function useCashTransactions(params?: {
  cashAccountId?: string;
  direction?: CashDirection;
  method?: PaymentMethod;
  categoryId?: string;
  counterpartyId?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
}) {
  const query = useInfiniteQuery({
    queryKey: ['cash-transactions', params],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) =>
      (
        await api.get<CashTxnPage>('/cash-transactions', {
          params: { ...params, limit: CASH_TXN_PAGE_SIZE, cursor: pageParam ?? undefined },
        })
      ).data,
    getNextPageParam: (last) => (last.pagination.hasMore ? last.pagination.cursor : undefined),
  });

  // Auto-advance through every remaining page so the table sees the whole set.
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    ...query,
    data: query.data?.pages.flatMap((p) => p.data),
    isLoadingAll: query.isLoading || query.hasNextPage,
  };
}

export function useCreateCashTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateCashTransactionInput) =>
      (
        await api.post<{ data: CashTransaction }>('/cash-transactions', input, {
          headers: { 'Idempotency-Key': randomUUID() },
        })
      ).data.data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cash-transactions'] });
      void qc.invalidateQueries({ queryKey: ['cash-accounts'] });
      void qc.invalidateQueries({ queryKey: ['cash-account'] });
    },
  });
}

export function useVoidCashTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) =>
      (await api.post<{ data: CashTransaction }>(`/cash-transactions/${id}/void`, { reason })).data.data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cash-transactions'] });
      void qc.invalidateQueries({ queryKey: ['cash-accounts'] });
      void qc.invalidateQueries({ queryKey: ['cash-account'] });
    },
  });
}

export type TurnoverRow = { categoryId: string; categoryName: string; direction: CashDirection; amount: string };
export function useCashTurnover(params?: { cashAccountId?: string; dateFrom?: string; dateTo?: string }) {
  return useQuery({
    queryKey: ['cash-turnover', params],
    queryFn: async () =>
      (
        await api.get<{ data: { rows: TurnoverRow[]; totalIncome: string; totalExpense: string } }>(
          '/cash-transactions/turnover',
          { params },
        )
      ).data.data,
  });
}
